import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';

type ButtonVariant = 'outlined' | 'none';

/**
 * A navigation control for paginating long lists of results.
 *
 * Use this component to move between pages of tabular data, cards, or any
 * other paginated collection. It renders previous/next buttons, numbered
 * page buttons, and an optional summary so users can understand where they
 * are in the result set.
 *
 * For accessibility, provide an accessible label via the default slot so
 * screen readers can identify it as pagination (for example,
 * "Results pagination"). All interactive controls are rendered as native
 * `<button>` elements and are fully keyboard-accessible.
 *
 * @element asc-pagination
 *
 * @fires asc-page-change - Fired when the user selects a different page or page size. Detail: `{ page: number; perPage: number }`
 *
 * @slot - Optional accessible label or description for the pagination control (e.g., "Results pagination").
 *
 * @csspart base - The root `<nav>` element that wraps the summary text and page controls.
 * @csspart prev - The "previous page" button.
 * @csspart next - The "next page" button.
 * @csspart page - An individual numbered page button.
 * @csspart separator - The non-interactive separator element shown between distant page ranges (for example, an ellipsis).
 * @csspart summary - The element that displays the current page and total results summary.
 *
 * @cssprop --asc-pagination-gap - Horizontal gap between the summary and the page controls. Default: `var(--asc-spacing-lg)`.
 * @cssprop --asc-pagination-button-padding - Padding applied to each Prev/Next button. Default: `var(--asc-spacing-md) var(--asc-spacing-lg)`.
 */
@customElement('asc-pagination')
export class AscPagination extends LitElement {
  static override styles = css`
    :host { display: block; container-type: inline-size; }
    nav {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      gap: var(--asc-pagination-gap, var(--asc-spacing-lg));
      width: 100%;
      box-sizing: border-box;
      position: relative;
      padding-top: 20px;
      padding-bottom: 12px;
      padding-left: 16px;
      padding-right: 16px;
      font-family: var(--asc-font-family-body, 'Aktiv Grotesk', sans-serif);
    }
    nav::before {
      content: '';
      position: absolute;
      top: 0;
      left: 16px;
      right: 16px;
      height: 1px;
      background: var(--asc-border-secondary, #a5b8bf);
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
      justify-self: center;
    }
    li { margin: 0; }

    /* Previous/Next buttons */
    button[part="prev"],
    button[part="next"] {
      font-family: var(--asc-font-family-body, 'Aktiv Grotesk', sans-serif);
      cursor: pointer;
      padding: var(--asc-pagination-button-padding, var(--asc-spacing-md) var(--asc-spacing-lg));
      border-radius: var(--asc-radius-sm, 8px);
      border: none;
      background: transparent;
      color: var(--asc-text-secondary, #30383b);
      display: flex;
      align-items: center;
      gap: 4px;
      box-shadow: none;
      justify-content: center;
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: 500;
      line-height: 20px;
    }
    button[part="prev"] {
      justify-self: start;
    }
    button[part="next"] {
      justify-self: end;
    }
    button[part="prev"]:hover,
    button[part="next"]:hover {
      background: var(--asc-bg-brand-primary, #e6f7f5);
      border: 2px solid var(--asc-accent-peak-green-fg, #009E86);
      color: var(--asc-accent-peak-green-fg, #009E86);
      padding: 6px 10px;
    }
    button[part="prev"]:focus-visible,
    button[part="next"]:focus-visible {
      outline: none;
      background: var(--asc-bg-brand-primary, #e6f7f5);
      border: 2px solid var(--asc-accent-peak-green-fg, #009E86);
      color: var(--asc-accent-peak-green-fg, #009E86);
      padding: 6px 10px;
      box-shadow: 0 0 0 2px var(--asc-color-base-white, white), 0 0 0 4px var(--asc-focus-ring, #009E86);
    }

    /* Outlined variant (default) */
    button[part="prev"][data-variant="outlined"],
    button[part="next"][data-variant="outlined"] {
      border: 1px solid var(--asc-border-secondary, #8ea2a9);
      background: var(--asc-color-base-white, white);
      color: var(--asc-text-secondary, #30383b);
      padding: 8px 12px;
      box-shadow: 0px 1px 2px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
    }
    button[part="prev"][data-variant="outlined"]:hover,
    button[part="next"][data-variant="outlined"]:hover {
      background: var(--asc-bg-secondary, #e6eaec);
    }
    button[part="prev"][data-variant="outlined"]:focus-visible,
    button[part="next"][data-variant="outlined"]:focus-visible {
      outline: none;
      background: var(--asc-bg-secondary, #e6eaec);
      box-shadow: 0px 1px 2px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05)), 0 0 0 2px var(--asc-color-base-white, white), 0 0 0 4px var(--asc-focus-ring, #009E86);
    }

    /* Unstyled variant */
    button[part="prev"][data-variant="none"],
    button[part="next"][data-variant="none"] {
      border: none;
      background: transparent;
      padding: 8px 4px;
      box-shadow: none;
    }
    button[part="prev"][data-variant="none"]:hover,
    button[part="next"][data-variant="none"]:hover {
      background: transparent;
    }

    .chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }
    .chevron svg {
      width: 24px;
      height: 20px;
      fill: currentColor;
    }

    /* Page number styles */
    button[part="page"] {
      width: var(--asc-spacing-5xl, 40px);
      height: var(--asc-spacing-5xl, 40px);
      padding: 0;
      display: grid;
      place-items: center;
      border-radius: var(--asc-radius-full, 9999px);
      background: transparent;
      color: var(--asc-text-quaternary, #627278);
      border: none;
      cursor: pointer;
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: 500;
      font-family: var(--asc-font-family-body, 'Aktiv Grotesk', sans-serif);
      line-height: 20px;
    }
    button[part="page"]:hover {
      background: var(--asc-color-base-white, white);
      border: 2px solid var(--asc-accent-peak-green-fg, #009E86);
      color: var(--asc-accent-peak-green-fg, #009E86);
    }
    /* Active page circle */
    button[part="page"][aria-current="page"] {
      width: var(--asc-spacing-5xl, 40px);
      height: var(--asc-spacing-5xl, 40px);
      padding: 0;
      display: grid;
      place-items: center;
      border-radius: var(--asc-radius-full, 9999px);
      color: var(--asc-text-quaternary, #627278);
      background: var(--asc-bg-secondary, #e6eaec);
      border: none;
      cursor: default;
    }
    button[part="page"][aria-current="page"]:hover {
      background: var(--asc-bg-secondary, #e6eaec);
    }

    /* Focus states for page numbers defined above */
    /* Non-active page: circle focus */
    button[part="page"]:focus-visible {
      outline: none;
      border-radius: var(--asc-radius-full, 9999px);
      background: var(--asc-color-base-white, white);
      border: 2px solid var(--asc-accent-peak-green-fg, #009E86);
      color: var(--asc-accent-peak-green-fg, #009E86);
      box-shadow: 0 0 0 2px var(--asc-color-base-white, white), 0 0 0 4px var(--asc-focus-ring, #009E86);
    }
    /* Active page: circle focus */
    button[part="page"][aria-current="page"]:focus-visible {
      outline: none;
      border-radius: var(--asc-radius-full, 9999px);
      background: var(--asc-bg-secondary, #e6eaec);
      border: none;
      color: var(--asc-text-quaternary, #627278);
      box-shadow: 0 0 0 2px var(--asc-color-base-white, white), 0 0 0 4px var(--asc-focus-ring, #009E86);
    }

    /* Separator */
    li[part="separator"] {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--asc-text-quaternary, #627278);
      padding: 0 4px;
      font-family: var(--asc-font-family-body, 'Aktiv Grotesk', sans-serif);
      font-size: var(--asc-font-size-text-sm, 14px);
      font-weight: 500;
      line-height: 20px;
    }

    /* Mobile: hide page numbers, show summary */
    @container (max-width: 560px) {
      nav {
        padding: 16px;
        gap: 8px;
      }
      ul.page-list { display: none; }
      .summary { display: block; grid-column: 2; justify-self: center; font-size: var(--asc-font-size-text-sm, 14px); font-weight: 500; font-family: var(--asc-font-family-body, 'Aktiv Grotesk', sans-serif); color: var(--asc-text-secondary, #30383b); line-height: 20px; }
      .btn-text { display: none; }
      /* Force outlined buttons on mobile */
      button[part="prev"],
      button[part="next"] {
        flex: 0 0 auto;
        width: var(--asc-spacing-5xl, 40px);
        height: var(--asc-spacing-5xl, 40px);
        padding: 0;
        gap: 0;
        border: 1px solid var(--asc-border-secondary, #8ea2a9) !important;
        background: var(--asc-color-base-white, white) !important;
        box-shadow: 0px 1px 2px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
        border-radius: var(--asc-radius-sm, 8px);
        display: grid;
        place-items: center;
      }
      button[part="prev"]:hover,
      button[part="next"]:hover {
        background: var(--asc-bg-secondary, #e6eaec) !important;
      }
    }
    @container (min-width: 561px) {
      .summary { display: none; }
    }

    /* Ensure summary shares the page-list grid column */
    .summary {
      grid-column: 2;
      justify-self: center;
    }

    /* Screen-reader-only label element */
    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `;

  /** Total number of results across all pages. */
  @property({ type: Number }) results = 0;
  /** Number of items shown per page. */
  @property({ type: Number, reflect: true, attribute: 'per-page' }) perPage = 10;
  /** 1-based active page index. Clamped to `[1, totalPages]`. */
  @property({ type: Number, reflect: true, attribute: 'current-page' }) currentPage = 1;
  /** Number of pages to display in the start/end anchor groups. */
  @property({ type: Number, reflect: true, attribute: 'display-radius' }) displayRadius = 3;
  /** Character shown when page range is collapsed. */
  @property({ type: String, attribute: 'page-separator' }) pageSeparator = '…';
  /** Visual style of Prev/Next buttons. `outlined` = bordered. `none` = transparent text. */
  @property({ type: String, reflect: true, attribute: 'button-variant' }) buttonVariant: ButtonVariant = 'outlined';
  /** Hides Prev/Next buttons entirely when `true`. */
  @property({ type: Boolean, reflect: true, attribute: 'hide-controls' }) hideControls = false;

  private _totalPages(): number {
    const rawPerPage = this.perPage;
    const safePerPage =
      Number.isFinite(rawPerPage) && rawPerPage > 0 ? rawPerPage : 1;
    return Math.max(1, Math.ceil(this.results / safePerPage));
  }

  private _clampPage(p: number): number {
    const t = this._totalPages();
    return Math.min(Math.max(1, Math.floor(p)), t);
  }

  private _emit(page: number) {
    dispatch<{ page: number; perPage: number }>(this, 'asc-page-change', { detail: { page, perPage: this.perPage } });
  }

  private _onPrev() {
    if (this.currentPage <= 1) return;
    const next = this._clampPage(this.currentPage - 1);
    this.currentPage = next;
    this._emit(next);
  }

  private _onNext() {
    const t = this._totalPages();
    if (this.currentPage >= t) return;
    const next = this._clampPage(this.currentPage + 1);
    this.currentPage = next;
    this._emit(next);
  }

  private _onPage(n: number) {
    const next = this._clampPage(n);
    this.currentPage = next;
    this._emit(next);
  }

  private _buildPages(): Array<number | 'sep'> {
    const total = this._totalPages();
    const radius = Math.max(0, Math.floor(this.displayRadius));
    const current = this._clampPage(this.currentPage);

    if (radius === 0) return [];

    const shown = new Set<number>();

    // Always show first and last page as anchors
    shown.add(1);
    shown.add(total);

    // Center a window of `radius` pages on the current page
    const half = Math.floor((radius - 1) / 2);
    let winStart = current - half;
    let winEnd = winStart + radius - 1;

    // Clamp window to valid range
    if (winStart < 1) {
      winStart = 1;
      winEnd = Math.min(total, radius);
    } else if (winEnd > total) {
      winEnd = total;
      winStart = Math.max(1, total - radius + 1);
    }

    for (let i = winStart; i <= winEnd; i++) shown.add(i);

    // Sort and insert separators where there are gaps
    const sorted = Array.from(shown).sort((a, b) => a - b);
    const pages: Array<number | 'sep'> = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] > sorted[i - 1] + 1) pages.push('sep');
      pages.push(sorted[i]);
    }

    return pages;
  }

  override updated(changed: PropertyValues<this>) {
    if (changed.has('results') || changed.has('perPage')) {
      const total = this._totalPages();
      if (this.currentPage > total) {
        const clamped = this._clampPage(this.currentPage);
        this.currentPage = clamped;
        this._emit(clamped);
      }
    }
  }

  override render() {
    const total = this._totalPages();
    const pages = this._buildPages();

    const atFirst = this.currentPage <= 1;
    const renderPrev = this.hideControls ? nothing : html`<button type="button" part="prev" data-variant=${this.buttonVariant} ?disabled=${atFirst} style=${atFirst ? 'visibility:hidden' : ''} @click=${this._onPrev} aria-label="Previous page"><span class="chevron"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><g transform="scale(-1,1) translate(-24,0)"><path d="M5.49805 24C5.29805 24 5.10905 23.922 4.96805 23.78C4.67605 23.488 4.67605 23.012 4.96805 22.719L15.688 12L4.96805 1.28C4.82605 1.139 4.74805 0.95 4.74805 0.75C4.74805 0.55 4.82605 0.361 4.96805 0.22C5.10905 0.078 5.29805 0 5.49805 0C5.69805 0 5.88705 0.078 6.02805 0.22L16.748 10.94C17.332 11.524 17.333 12.475 16.749 13.059L6.02805 23.78C5.88705 23.922 5.69805 24 5.49805 24Z" fill="currentColor"/></g></svg></span> <span class="btn-text">Previous</span></button>`;

    const atLast = this.currentPage >= total;
    const renderNext = this.hideControls ? nothing : html`<button type="button" part="next" data-variant=${this.buttonVariant} ?disabled=${atLast} style=${atLast ? 'visibility:hidden' : ''} @click=${this._onNext} aria-label="Next page"><span class="btn-text">Next</span> <span class="chevron"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5.49805 24C5.29805 24 5.10905 23.922 4.96805 23.78C4.67605 23.488 4.67605 23.012 4.96805 22.719L15.688 12L4.96805 1.28C4.82605 1.139 4.74805 0.95 4.74805 0.75C4.74805 0.55 4.82605 0.361 4.96805 0.22C5.10905 0.078 5.29805 0 5.49805 0C5.69805 0 5.88705 0.078 6.02805 0.22L16.748 10.94C17.332 11.524 17.333 12.475 16.749 13.059L6.02805 23.78C5.88705 23.922 5.69805 24 5.49805 24Z" fill="currentColor"/></svg></span></button>`;


    return html`
      <nav part="base" role="navigation" aria-labelledby="nav-label" @keydown=${this._onKeyDown}>
        <span id="nav-label" class="visually-hidden"><slot>Pagination</slot></span>
        ${renderPrev}

        <ul class="page-list" role="list">
          ${pages.map(p => p === 'sep'
            ? html`<li part="separator">${this.pageSeparator}</li>`
            : html`<li><button type="button" part="page" ?disabled=${p === this.currentPage} aria-current=${p === this.currentPage ? 'page' : nothing} @click=${() => this._onPage(p as number)}>${p}</button></li>`) }
        </ul>

        <div class="summary" part="summary">Page ${this.currentPage} of ${total}</div>

        ${renderNext}
      </nav>`;
  }

  private _onKeyDown(e: KeyboardEvent) {
    const k = e.key;

    const focusable = Array.from(this.renderRoot.querySelectorAll('button:not([disabled])')) as HTMLElement[];
    if (!focusable.length) return;

    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    const idx = active ? focusable.indexOf(active) : -1;

    if (k === 'ArrowLeft') {
      e.preventDefault();
      const prev = idx > 0 ? focusable[idx - 1] : focusable[0];
      prev?.focus();
    } else if (k === 'ArrowRight') {
      e.preventDefault();
      const next = idx >= 0 && idx < focusable.length - 1 ? focusable[idx + 1] : focusable[focusable.length - 1];
      next?.focus();
    } else if (k === 'Home') {
      e.preventDefault();
      focusable[0]?.focus();
    } else if (k === 'End') {
      e.preventDefault();
      focusable[focusable.length - 1]?.focus();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-pagination': AscPagination;
  }
}
